import { queryOptions, type Query, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { CAPACITY_MEMBER_PAGE_SIZE, CAPACITY_TASK_PAGE_SIZE } from "@/lib/capacity-pagination";
import type { WorkspaceCapacityService } from "@/services/workspace-capacity.service";
import { GET_TIMEOUT_MS } from "@/services/api.service";
import type { TTaskToUsersMatchResponse } from "@/types/task-matching";
import type {
  TWorkspaceCapacityAllocation,
  TWorkspaceCapacityGroupBy,
  TWorkspaceCapacityMember,
  TWorkspaceCapacityMemberDay,
  TWorkspaceCapacitySummary,
} from "@/types/workspace-capacity";
import { FRESHNESS, withinAttempt, type ReadRequest } from "./policies";
import { rereadOlderThan } from "./reread";
import { awaitScheduleHint } from "./schedule-invalidation";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The capacity grid's reads (NG-2812): the People view on the Capacity page,
 * a team's Capacity tab, a project's Resources tab and the dashboard member
 * panel all draw from these.
 *
 * - `summary`: the grid itself, `[slug, uid, 'capacity', 'summary', {…}]`. Its
 *   first page is the collapsed group list (or the first page of people when
 *   grouped by person); `pages` more pages of groups or people are appended.
 * - `team-members`: one expanded group's people, `[…, 'team-members', {…}]`.
 * - `member-tasks`: one expanded person's task rows, `[…, 'member-tasks', {…}]`.
 *
 * Every key carries the whole request, window and filters included, so two
 * embeds asking the same question share an entry and a different one never
 * answers it. `pages` is how many pages the view asked for. Asking for one more
 * reuses the fresh entry for the pages already read and fetches only the next,
 * so "Show more" costs one page, as it did in the store.
 *
 * Freshness: `mutable`, without the 60 s visible poll. These reads are
 * rollup-backed and the store never polled them. A write anywhere in the app
 * re-reads them (`schedule-invalidation.ts`), and so does the backend's
 * `capacity:list` hint once a rebuild lands (`cache-hints/capacity.ts`).
 *
 * @writers `capacityWriteLanded` (the grid's own drag, unassign, reassign and
 * peek writes), `rereadCapacity` (the unassign settle loop), `installScheduleWriteInvalidation` (any write whose URL can move
 * a bar), the `capacity:list` hint, and `rescheduleInCapacityCache` (the
 * optimistic drag).
 */

/** The toolbar filters as the server reads them. The store sorts every list. */
export interface CapacityFilters {
  teamIds?: string[];
  requiredSkillIds?: string[];
  projectIds?: string[];
  programIds?: string[];
  projectStateIds?: string[];
  jobRoleIds?: string[];
  includeArchivedProjects?: boolean;
  statusFilter?: string;
  search?: string;
}

type CapacityRowGroup = "member" | "team" | "project";

/** The range a read covers. Every capacity answer is for one window. */
export interface CapacityWindowIso {
  readonly startIso: string;
  readonly endIso: string;
}

export interface CapacitySummaryParams extends CapacityWindowIso {
  zoom: TWorkspaceCapacityGroupBy;
  rowGroup: CapacityRowGroup;
  filters: CapacityFilters;
  pages: number;
}

export interface CapacityTeamMembersParams extends CapacityWindowIso {
  zoom: TWorkspaceCapacityGroupBy;
  teamId: string;
  memberIds: string[];
  filters: CapacityFilters;
  pages: number;
}

export interface CapacityMemberTasksParams extends CapacityWindowIso {
  memberId: string;
  filters: CapacityFilters;
  pages: number;
}

/** An answer with the window it describes, so a carried-over one can be placed. */
export interface CapacitySummaryData {
  readonly window: CapacityWindowIso;
  readonly summary: TWorkspaceCapacitySummary;
}

export interface CapacityTeamMembersData {
  readonly window: CapacityWindowIso;
  readonly members: TWorkspaceCapacityMember[];
  /** The next member page's cursor, or `null` when none remain. */
  readonly nextCursor: string | null;
}

export interface CapacityMemberTasksData {
  readonly window: CapacityWindowIso;
  readonly days: TWorkspaceCapacityMemberDay[];
  readonly hasNext: boolean;
}

type CapacityService = Pick<
  WorkspaceCapacityService,
  "getWorkspaceCapacity" | "getWorkspaceCapacityDetails"
>;

const POLICY = { ...FRESHNESS.mutable, refetchInterval: false } as const;

/** Every capacity read in `scope`, the prefix a write invalidates. */
export function capacityPrefix(scope: QueryScope): QueryKey {
  return scopedKey(scope, "capacity");
}

export function capacitySummaryKey(scope: QueryScope, params: CapacitySummaryParams) {
  return scopedKey(scope, "capacity", "summary", {
    start: params.startIso,
    end: params.endIso,
    zoom: params.zoom,
    row_group: params.rowGroup,
    filters: params.filters,
    pages: params.pages,
  });
}

export function capacityTeamMembersKey(scope: QueryScope, params: CapacityTeamMembersParams) {
  return scopedKey(scope, "capacity", "team-members", {
    start: params.startIso,
    end: params.endIso,
    zoom: params.zoom,
    team: params.teamId,
    member_ids: params.memberIds,
    filters: params.filters,
    pages: params.pages,
  });
}

export function capacityMemberTasksKey(scope: QueryScope, params: CapacityMemberTasksParams) {
  return scopedKey(scope, "capacity", "member-tasks", {
    start: params.startIso,
    end: params.endIso,
    member: params.memberId,
    filters: params.filters,
    pages: params.pages,
  });
}

/**
 * The cached answer under `key` while it is still fresh: present, not
 * invalidated and inside the reuse window. A longer walk starts from it rather
 * than re-reading the pages it already holds. Anything a write invalidated is
 * read again from the first page, which is the re-walk the store did on a
 * forced refresh.
 */
function freshData<T>(client: QueryClient, key: QueryKey): T | undefined {
  const state = client.getQueryState<T>(key);
  if (!state?.data || state.isInvalidated) return undefined;
  if (Date.now() - state.dataUpdatedAt >= POLICY.staleTime) return undefined;
  return state.data;
}

function summaryRequest(params: CapacitySummaryParams) {
  return {
    includeTotals: false,
    // Groups load their people when expanded (`team-members`); the first read
    // is the collapsed list. Grouped by person there are no groups.
    includeGroupMembers: false,
    // Grouped, the flat person rows are not drawn, so skip computing them.
    ...(params.rowGroup === "member" ? {} : { includeMembers: false }),
    includeMemberMetrics: false,
    memberPageSize: CAPACITY_MEMBER_PAGE_SIZE,
    rowGroup: params.rowGroup,
    // Project grouping lists every project (the project picker's cap).
    ...(params.rowGroup === "project" ? { groupPageSize: 100 } : {}),
    ...params.filters,
  };
}

/** The next page of groups or people after `summary`, merged onto it. */
async function appendSummaryPage(
  service: CapacityService,
  scope: QueryScope,
  params: CapacitySummaryParams,
  summary: TWorkspaceCapacitySummary,
  request: ReadRequest,
): Promise<TWorkspaceCapacitySummary | null> {
  if (params.rowGroup === "member") {
    const pagination = summary.members_pagination;
    if (!pagination?.has_next) return null;
    const next = await service.getWorkspaceCapacity(
      scope.tenant,
      params.startIso,
      params.endIso,
      params.zoom,
      {
        includeTotals: false,
        includeMemberMetrics: false,
        memberPageSize: pagination.page_size,
        memberCursor: pagination.next_cursor,
        rowGroup: params.rowGroup,
        ...params.filters,
      },
      request,
    );
    return {
      ...summary,
      members: [...(summary.members ?? []), ...(next.members ?? [])],
      members_pagination: next.members_pagination,
    };
  }
  const pagination = summary.groups_pagination;
  if (!pagination?.has_next) return null;
  // A group page carries no people, so the day grain costs nothing extra; it is
  // the request the store made.
  const next = await service.getWorkspaceCapacity(
    scope.tenant,
    params.startIso,
    params.endIso,
    "day",
    {
      includeTotals: false,
      includeGroupMembers: false,
      includeMembers: false,
      includeMemberMetrics: false,
      rowGroup: params.rowGroup,
      groupPageSize: pagination.page_size,
      groupCursor: pagination.next_cursor,
      ...params.filters,
    },
    request,
  );
  return {
    ...summary,
    groups: [...(summary.groups ?? []), ...(next.groups ?? [])],
    groups_pagination: next.groups_pagination,
  };
}

/** The grid: the first page of groups or people, then `pages - 1` more. */
export function capacitySummaryQuery(
  service: CapacityService,
  client: QueryClient,
  scope: QueryScope,
  params: CapacitySummaryParams,
) {
  const window = { startIso: params.startIso, endIso: params.endIso };
  return queryOptions({
    ...POLICY,
    queryKey: capacitySummaryKey(scope, params),
    queryFn: scopedRead<CapacitySummaryData>(
      (request) =>
        withinAttempt(request, async (bounded) => {
          const prior =
            params.pages > 1
              ? freshData<CapacitySummaryData>(
                  client,
                  capacitySummaryKey(scope, { ...params, pages: params.pages - 1 }),
                )
              : undefined;
          let summary =
            prior?.summary ??
            (await service.getWorkspaceCapacity(
              scope.tenant,
              params.startIso,
              params.endIso,
              params.zoom,
              summaryRequest(params),
              bounded,
            ));
          for (let page = prior ? params.pages - 1 : 1; page < params.pages; page += 1) {
            const next = await appendSummaryPage(service, scope, params, summary, bounded);
            if (!next) break;
            summary = next;
          }
          return { window, summary };
        }),
      // A large workspace's walk outlasts the ten-second interactive deadline
      // (10–16 s on dev), and a timeout is final, so it gets the GET timeout the
      // store-era read had (NG-3310).
      { retry: "safe-read", deadlineMs: GET_TIMEOUT_MS },
    ),
  });
}

function nextMemberCursor(summary: TWorkspaceCapacitySummary): string | null {
  const pagination = summary.members_pagination;
  return pagination?.has_next ? (pagination.next_cursor ?? null) : null;
}

/** One expanded group's people, `pages` member pages of it. */
export function capacityTeamMembersQuery(
  service: CapacityService,
  client: QueryClient,
  scope: QueryScope,
  params: CapacityTeamMembersParams,
) {
  const window = { startIso: params.startIso, endIso: params.endIso };
  return queryOptions({
    ...POLICY,
    queryKey: capacityTeamMembersKey(scope, params),
    queryFn: scopedRead<CapacityTeamMembersData>(
      (request) =>
        withinAttempt(request, async (bounded) => {
          // An empty group has no one to ask about.
          if (params.memberIds.length === 0) return { window, members: [], nextCursor: null };
          const prior =
            params.pages > 1
              ? freshData<CapacityTeamMembersData>(
                  client,
                  capacityTeamMembersKey(scope, { ...params, pages: params.pages - 1 }),
                )
              : undefined;
          const byId = new Map((prior?.members ?? []).map((member) => [member.id, member]));
          let cursor: string | null = prior ? prior.nextCursor : null;
          let page = prior ? params.pages - 1 : 0;
          if (prior && cursor === null) return prior;
          do {
            const summary = await service.getWorkspaceCapacity(
              scope.tenant,
              params.startIso,
              params.endIso,
              params.zoom,
              {
                includeTotals: false,
                rowGroup: "member",
                memberIds: params.memberIds,
                memberPageSize: CAPACITY_MEMBER_PAGE_SIZE,
                memberCursor: cursor,
                ...params.filters,
              },
              bounded,
            );
            for (const member of summary.members ?? []) byId.set(member.id, member);
            cursor = nextMemberCursor(summary);
            page += 1;
          } while (cursor && page < params.pages);
          return { window, members: Array.from(byId.values()), nextCursor: cursor };
        }),
      { retry: "safe-read" },
    ),
  });
}

/** The person's day slices in a details answer, whichever shape it came in. */
function memberDaysOf(
  summary: TWorkspaceCapacitySummary,
  memberId: string,
): TWorkspaceCapacityMemberDay[] {
  const members = summary.members?.length
    ? summary.members
    : (summary.groups ?? []).flatMap((group) => group.members ?? []);
  return members.find((member) => member.id === memberId)?.days ?? [];
}

/**
 * Append a later task page's day slices onto the loaded ones. Pages cover
 * disjoint work items, so allocations concatenate without de-duping.
 */
function mergeMemberDays(
  existing: TWorkspaceCapacityMemberDay[],
  incoming: TWorkspaceCapacityMemberDay[],
): TWorkspaceCapacityMemberDay[] {
  if (existing.length === 0) return incoming;
  const byDate = new Map<string, TWorkspaceCapacityMemberDay>(
    existing.map((day) => [day.date, { ...day, allocations: [...(day.allocations ?? [])] }]),
  );
  for (const day of incoming) {
    const current = byDate.get(day.date);
    if (current) current.allocations = [...(current.allocations ?? []), ...(day.allocations ?? [])];
    else byDate.set(day.date, day);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/** One expanded person's task rows, `pages` task pages of them. */
export function capacityMemberTasksQuery(
  service: CapacityService,
  client: QueryClient,
  scope: QueryScope,
  params: CapacityMemberTasksParams,
) {
  const window = { startIso: params.startIso, endIso: params.endIso };
  return queryOptions({
    ...POLICY,
    queryKey: capacityMemberTasksKey(scope, params),
    queryFn: scopedRead<CapacityMemberTasksData>(
      (request) =>
        withinAttempt(request, async (bounded) => {
          const prior =
            params.pages > 1
              ? freshData<CapacityMemberTasksData>(
                  client,
                  capacityMemberTasksKey(scope, { ...params, pages: params.pages - 1 }),
                )
              : undefined;
          if (prior && !prior.hasNext) return prior;
          let days = prior?.days ?? [];
          let hasNext = true;
          for (let page = prior ? params.pages - 1 : 0; page < params.pages && hasNext; page += 1) {
            const summary = await service.getWorkspaceCapacityDetails(
              scope.tenant,
              params.startIso,
              params.endIso,
              "day",
              {
                ...params.filters,
                memberIds: [params.memberId],
                taskOffset: page * CAPACITY_TASK_PAGE_SIZE,
                taskLimit: CAPACITY_TASK_PAGE_SIZE,
              },
              bounded,
            );
            days = mergeMemberDays(days, memberDaysOf(summary, params.memberId));
            hasNext = summary.tasks_pagination?.has_next ?? false;
          }
          return { window, days, hasNext };
        }),
      { retry: "safe-read" },
    ),
  });
}

/** The representation object of a capacity key, or `null` for another key. */
function representationOf(
  queryKey: QueryKey,
  scope: QueryScope,
  family: string,
): Record<string, unknown> | null {
  if (queryKey[0] !== scope.tenant || queryKey[1] !== scope.principal) return null;
  if (queryKey[2] !== "capacity" || queryKey[3] !== family) return null;
  const representation = queryKey[4];
  return representation && typeof representation === "object"
    ? (representation as Record<string, unknown>)
    : null;
}

/** A representation without what a carried-over answer may differ in. */
function shapeOf(representation: Record<string, unknown>): string {
  const { start: _start, end: _end, pages: _pages, ...shape } = representation;
  return JSON.stringify(shape);
}

/**
 * The most recently read answer of `family` with the same shape as `key`:
 * the same question about another window, or about fewer pages.
 *
 * It is what the grid shows while the asked-for answer is on its way, so a
 * date change keeps the same people, teams and expansion on screen and swaps
 * them in place instead of dropping the grid to a skeleton. The answer carries
 * the window it describes, so the rows are placed against the range they belong
 * to, not the one the toolbar names.
 */
export function latestSameShape<T>(
  client: QueryClient,
  scope: QueryScope,
  family: "summary" | "team-members" | "member-tasks",
  key: QueryKey,
): T | undefined {
  const wanted = representationOf(key, scope, family);
  if (!wanted) return undefined;
  const shape = shapeOf(wanted);
  let best: { data: T; at: number } | undefined;
  for (const query of client
    .getQueryCache()
    .findAll({ queryKey: [scope.tenant, scope.principal, "capacity", family] })) {
    const data = query.state.data as T | undefined;
    if (data === undefined) continue;
    const representation = representationOf(query.queryKey, scope, family);
    if (!representation || shapeOf(representation) !== shape) continue;
    if (!best || query.state.dataUpdatedAt > best.at)
      best = { data, at: query.state.dataUpdatedAt };
  }
  return best?.data;
}

/** True for every capacity read in `scope`. */
export function isCapacityRead(scope: QueryScope): (query: Query) => boolean {
  return ({ queryKey }) =>
    queryKey[0] === scope.tenant && queryKey[1] === scope.principal && queryKey[2] === "capacity";
}

/**
 * One of the grid's own writes landed: re-read capacity once the rebuild it
 * started has landed (`awaitScheduleHint`). The HTTP layer has already
 * announced the write, so this joins that wait rather than adding a read.
 */
export function capacityWriteLanded(client: QueryClient): void {
  awaitScheduleHint(client);
}

/**
 * Re-read every capacity answer on screen in `scope` read before now, and mark
 * the rest stale, without cancelling a read in flight (`reread.ts`). For a
 * check that is not a write's: the unassign settle loop (NG-2659).
 */
export function rereadCapacity(client: QueryClient, scope: QueryScope): Promise<void> {
  return rereadOlderThan(client, isCapacityRead(scope), Date.now());
}

/** Re-read one person's task rows in the window on screen, whatever their freshness. */
export function invalidateMemberTasks(
  client: QueryClient,
  scope: QueryScope,
  window: CapacityWindowIso,
  memberId: string,
): Promise<void> {
  return rereadOlderThan(
    client,
    ({ queryKey }) => {
      const representation = representationOf(queryKey, scope, "member-tasks");
      return (
        representation !== null &&
        representation.member === memberId &&
        representation.start === window.startIso &&
        representation.end === window.endIso
      );
    },
    Date.now(),
  );
}

/** Move `workItemId`'s bars in `days`, or return `days` itself when it has none. */
function moveInDays<D extends TWorkspaceCapacityMemberDay[] | undefined>(
  days: D,
  workItemId: string,
  startIso: string,
  targetIso: string,
): D {
  if (!days?.some((day) => (day.allocations ?? []).some((a) => a.id === workItemId))) return days;
  const move = (allocation: TWorkspaceCapacityAllocation) =>
    allocation.id === workItemId
      ? { ...allocation, start_date: startIso, target_date: targetIso }
      : allocation;
  return days.map((day) =>
    (day.allocations ?? []).some((a) => a.id === workItemId)
      ? { ...day, allocations: (day.allocations ?? []).map(move) }
      : day,
  ) as D;
}

function moveInMembers(
  members: TWorkspaceCapacityMember[] | undefined,
  workItemId: string,
  startIso: string,
  targetIso: string,
): TWorkspaceCapacityMember[] | undefined {
  if (!members) return members;
  let changed = false;
  const next = members.map((member) => {
    const days = moveInDays(member.days, workItemId, startIso, targetIso);
    if (days === member.days) return member;
    changed = true;
    return { ...member, days };
  });
  return changed ? next : members;
}

/** `data` with the work item's bar moved, or `data` itself when it holds none. */
function moveInAnswer(data: unknown, workItemId: string, startIso: string, targetIso: string) {
  if (!data || typeof data !== "object") return data;
  if ("summary" in data) {
    const { summary } = data as CapacitySummaryData;
    const members = moveInMembers(summary.members, workItemId, startIso, targetIso);
    return members === summary.members ? data : { ...data, summary: { ...summary, members } };
  }
  if ("members" in data) {
    const answer = data as CapacityTeamMembersData;
    const members = moveInMembers(answer.members, workItemId, startIso, targetIso);
    return members === answer.members ? data : { ...answer, members };
  }
  if ("days" in data) {
    const answer = data as CapacityMemberTasksData;
    const days = moveInDays(answer.days, workItemId, startIso, targetIso);
    return days === answer.days ? data : { ...answer, days };
  }
  return data;
}

/**
 * Move a work item's bar to new dates in every cached capacity answer, so a
 * drag lands at once instead of waiting on the server. Reads in flight are
 * cancelled first, so a late one cannot draw the bar back where it was.
 *
 * Returns the undo for a failed write. It puts back only the answers this
 * patched, and only where nothing has replaced them since: a re-read that
 * landed in between is newer than either. Heat cells keep the old day
 * distribution until the re-read after the write; only the bar reads these
 * dates.
 */
export function rescheduleInCapacityCache(
  client: QueryClient,
  scope: QueryScope,
  workItemId: string,
  startIso: string,
  targetIso: string,
): () => void {
  void client.cancelQueries({ queryKey: capacityPrefix(scope) });
  const patched: { key: QueryKey; before: unknown; after: unknown }[] = [];
  for (const [key, before] of client.getQueriesData({ queryKey: capacityPrefix(scope) })) {
    const after = moveInAnswer(before, workItemId, startIso, targetIso);
    if (after === before) continue;
    client.setQueryData(key, after);
    // What the cache holds, which structural sharing may have rebuilt from `after`.
    patched.push({ key, before, after: client.getQueryData(key) });
  }
  return () => {
    for (const { key, before, after } of patched) {
      if (client.getQueryData(key) === after) client.setQueryData(key, before);
    }
  };
}

export interface CapacityMembersWindowParams extends CapacityWindowIso {
  memberIds: string[];
}

function capacityMembersWindowKey(scope: QueryScope, params: CapacityMembersWindowParams) {
  return scopedKey(scope, "capacity", "members-window", {
    start: params.startIso,
    end: params.endIso,
    member_ids: params.memberIds,
  });
}

/**
 * Named people's capacity over one window, all of them on one page: the
 * work-item panel's assignee hints. The lean rollup-backed read; the
 * per-allocation details endpoint ran the multi-second legacy compute path for
 * the same numbers.
 */
export function capacityMembersWindowQuery(
  service: Pick<WorkspaceCapacityService, "getWorkspaceCapacity">,
  scope: QueryScope,
  params: CapacityMembersWindowParams,
) {
  return queryOptions({
    ...POLICY,
    queryKey: capacityMembersWindowKey(scope, params),
    queryFn: scopedRead<TWorkspaceCapacitySummary>(
      (request) =>
        service.getWorkspaceCapacity(
          scope.tenant,
          params.startIso,
          params.endIso,
          "day",
          {
            includeTotals: false,
            includeMemberMetrics: false,
            memberIds: params.memberIds,
            memberPageSize: Math.max(params.memberIds.length, 1),
          },
          request,
        ),
      { retry: "safe-read" },
    ),
  });
}

export interface TaskMatchesParams extends CapacityWindowIso {
  workItemId: string;
  limit: number;
  candidateScope: "project" | "workspace";
}

function taskMatchesKey(scope: QueryScope, params: TaskMatchesParams) {
  return scopedKey(scope, "capacity", "task-matches", {
    work_item: params.workItemId,
    start: params.startIso,
    end: params.endIso,
    limit: params.limit,
    candidate_scope: params.candidateScope,
  });
}

/** How often an open work item re-ranks who could take it over, while the tab is visible. */
const TASK_MATCHES_POLL_MS = 30_000;

/**
 * Who best fits a work item over its dates. The panel's replacement
 * candidates read it, polled every 30 s while the tab is visible, as before
 * (NG-2816): a hidden tab neither reads nor polls.
 */
export function taskMatchesQuery(
  service: Pick<WorkspaceCapacityService, "getUserMatchesForTask">,
  scope: QueryScope,
  params: TaskMatchesParams,
) {
  return queryOptions({
    ...POLICY,
    refetchInterval: TASK_MATCHES_POLL_MS,
    queryKey: taskMatchesKey(scope, params),
    queryFn: scopedRead<TTaskToUsersMatchResponse>(
      (request) =>
        service.getUserMatchesForTask(
          scope.tenant,
          {
            workItemId: params.workItemId,
            startDate: params.startIso,
            endDate: params.endIso,
            limit: params.limit,
            candidateScope: params.candidateScope,
          },
          request,
        ),
      { retry: "safe-read" },
    ),
  });
}

export interface CapacityMemberDetailParams extends CapacityWindowIso {
  zoom: TWorkspaceCapacityGroupBy;
  memberId: string;
}

function capacityMemberDetailKey(scope: QueryScope, params: CapacityMemberDetailParams) {
  return scopedKey(scope, "capacity", "member-detail", {
    start: params.startIso,
    end: params.endIso,
    zoom: params.zoom,
    member: params.memberId,
  });
}

/** One person's capacity with every allocation's detail: the grid's member panel. */
export function capacityMemberDetailQuery(
  service: Pick<WorkspaceCapacityService, "getWorkspaceCapacity">,
  scope: QueryScope,
  params: CapacityMemberDetailParams,
) {
  return queryOptions({
    ...POLICY,
    queryKey: capacityMemberDetailKey(scope, params),
    queryFn: scopedRead<TWorkspaceCapacityMember | null>(
      async (request) => {
        const detailed = await service.getWorkspaceCapacity(
          scope.tenant,
          params.startIso,
          params.endIso,
          params.zoom,
          {
            includeTotals: false,
            includeDetails: true,
            memberIds: [params.memberId],
            memberPageSize: 1,
          },
          request,
        );
        return (detailed.members ?? []).find((m) => m.id === params.memberId) ?? null;
      },
      { retry: "safe-read" },
    ),
  });
}
